import { Injectable } from '@angular/core';
import { AuthenticationService, DivisionRole, OrganizationLink, User } from '@damofire/data-security';
import { BehaviorSubject, Observable } from 'rxjs';
import { Warehouse } from '@damofire/data-watson';
import { collection, documentId, getDocs, getFirestore, query, QuerySnapshot, where } from '@angular/fire/firestore';
import { snapToDto, WW_WAREHOUSES_COLLECTION } from '@damofire/data-commons';

@Injectable()
export class WarehouseSelectorService {
  private readonly warehousesSubject: BehaviorSubject<Warehouse[]> = new BehaviorSubject<Warehouse[]>([]);
  readonly $warehouses: Observable<Warehouse[]>;

  constructor(private readonly authService: AuthenticationService) {
    this.$warehouses = this.warehousesSubject.asObservable();
    authService.subscribe({
      next: (user) => this.onAuthChanged(user).catch(console.error),
    });
  }

  async onAuthChanged(user: User): Promise<void> {
    if (!user) {
      this.warehousesSubject.next([]);
    } else {
      console.time(`Collecting warehouses to build navigation items.`);
      const divisionLinks: DivisionRole[] = await this.authService.divisions();
      const orgAdmins: OrganizationLink[] = this.authService.administrating();

      const orgIds = orgAdmins.map((l: OrganizationLink) => l._id);
      const organizationWarehouses: Warehouse[] = await this.searchEnabledByOrgIds(orgIds);

      const linkIds = divisionLinks.map((l: DivisionRole) => l._id).filter((w: string) => !organizationWarehouses.some((l: Warehouse) => l._id === w));
      const linksWarehouses: Warehouse[] = await this.searchEnabledByIds(linkIds);

      const allTogether = linksWarehouses.concat(...organizationWarehouses);
      allTogether.sort((d1: Warehouse, d2: Warehouse) => {
        let res = d1.organization?.name?.localeCompare(d2.organization?.name);
        if (res === 0) {
          res = d1.name?.localeCompare(d2.name);
        }

        return res;
      });

      console.timeEnd(`Collecting warehouses to build navigation items.`);
      this.warehousesSubject.next(allTogether);
    }
  }

  private searchEnabledByOrgIds(orgIds: string[]): Promise<Warehouse[]> {
    const promises = [];
    while (orgIds.length) {
      const subOrgIds = orgIds.splice(0, 10);
      promises.push(getDocs(query(collection(getFirestore(), WW_WAREHOUSES_COLLECTION), where('organization._id', 'in', subOrgIds), where('enabled', '==', true))));
    }

    return Promise.all(promises).then((all: QuerySnapshot[]) => {
      return all.reduce((warehouses: Warehouse[], querySnapshot: QuerySnapshot) => {
        warehouses.push(...querySnapshot.docs.map(snapToDto));
        return warehouses;
      }, [] as Warehouse[]);
    });
  }

  private searchEnabledByIds(ids: string[]): Promise<Warehouse[]> {
    const promises = [];
    while (ids.length) {
      const subIds = ids.splice(0, 10);
      promises.push(getDocs(query(collection(getFirestore(), WW_WAREHOUSES_COLLECTION), where(documentId(), 'in', subIds), where('enabled', '==', true))));
    }

    return Promise.all(promises).then((all: QuerySnapshot[]) => {
      return all.reduce((warehouses: Warehouse[], querySnapshot: QuerySnapshot) => {
        warehouses.push(...querySnapshot.docs.map(snapToDto));
        return warehouses;
      }, [] as Warehouse[]);
    });
  }
}
